import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, PhoneCall, Lock, FilePlus2, History } from "lucide-react";
import api from "@/lib/api";
import {
  Panel, StatusPill, EmptyState, Loading, SectionLabel,
} from "@/components/app/primitives";
import { moneyCompact, moneyFull, pct, shortDay, EMPTY_MARK } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STAGES = ["Sourced", "Contacted", "In diligence", "Soft-circled", "Committed", "Passed"];
const INSTRUMENTS = ["SAFE", "Convertible note", "Equity", "Priced round"];

function stageTone(stage) {
  if (stage === "Committed") return "on_track";
  if (stage === "Soft-circled" || stage === "In diligence") return "accent";
  if (stage === "Passed") return "blocked";
  return "neutral";
}

// ---- Raise tracker -----------------------------------------------------------
function RaiseEditDialog({ raise, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: raise.name || "",
    target: raise.target, committed: raise.committed,
    in_diligence: raise.in_diligence, soft_circled: raise.soft_circled,
    pre_money_valuation: raise.pre_money_valuation,
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const num = (k) => (e) => set(k, e.target.value === "" ? 0 : parseFloat(e.target.value));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/capital/raise", f);
      onSaved(res.data);
      toast.success("Raise updated");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the raise");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="raise-edit-button" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
          <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} /> Edit raise
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-lg">Edit the active raise</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Round name</Label>
            <Input value={f.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Target" v={f.target} onChange={num("target")} />
            <Field label="Committed" v={f.committed} onChange={num("committed")} />
            <Field label="In diligence" v={f.in_diligence} onChange={num("in_diligence")} />
            <Field label="Soft-circled" v={f.soft_circled} onChange={num("soft_circled")} />
          </div>
          <Field label="Pre-money valuation (placeholder)" v={f.pre_money_valuation} onChange={num("pre_money_valuation")} />
        </div>
        <DialogFooter>
          <Button data-testid="raise-save" onClick={save} disabled={saving} className="bg-primary hover:bg-[#2746c0]">
            {saving ? "Saving" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, v, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input type="number" value={v ?? 0} onChange={onChange} className="font-mono" />
    </div>
  );
}

function RaiseTracker({ raise, onSaved }) {
  const cur = raise.currency || "SGD";
  const target = raise.target || 0;
  const w = (n) => (target ? `${Math.min((n / target) * 100, 100)}%` : "0%");
  const identified = (raise.committed || 0) + (raise.in_diligence || 0) + (raise.soft_circled || 0);
  const gap = Math.max(target - (raise.committed || 0), 0);

  return (
    <Panel
      title="Raise tracker"
      testId="raise-tracker"
      action={raise.can_edit ? <RaiseEditDialog raise={raise} onSaved={onSaved} /> : null}
    >
      <div className="p-4">
        <div className="flex items-end justify-between">
          <div>
            <SectionLabel>{raise.name || "Active raise"}, committed of target</SectionLabel>
            <p className="mt-1 font-mono tabular-nums text-2xl text-foreground leading-none">
              {moneyCompact(raise.committed, cur)} <span className="text-muted-foreground text-base">/ {moneyCompact(target, cur)}</span>
            </p>
          </div>
          <span className="font-mono tabular-nums text-sm text-muted-foreground">{pct(target ? (raise.committed / target) * 100 : 0, 0)}</span>
        </div>

        {/* Stacked progress: committed, in diligence, soft-circled */}
        <div className="mt-4 h-3 w-full rounded-full bg-white/8 overflow-hidden flex">
          <div className="h-full bg-primary" style={{ width: w(raise.committed) }} title="Committed" data-testid="raise-bar-committed" />
          <div className="h-full bg-[rgba(43,79,216,0.5)]" style={{ width: w(raise.in_diligence) }} title="In diligence" />
          <div className="h-full bg-[rgba(43,79,216,0.28)]" style={{ width: w(raise.soft_circled) }} title="Soft-circled" />
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
          <Breakdown label="Committed" value={moneyFull(raise.committed, cur)} dot="bg-primary" />
          <Breakdown label="In diligence" value={moneyFull(raise.in_diligence, cur)} dot="bg-[rgba(43,79,216,0.5)]" />
          <Breakdown label="Soft-circled" value={moneyFull(raise.soft_circled, cur)} dot="bg-[rgba(43,79,216,0.28)]" />
          <Breakdown label="Identified" value={moneyFull(identified, cur)} />
          <Breakdown label="Target" value={moneyFull(target, cur)} />
          <Breakdown label="Gap to target" value={moneyFull(gap, cur)} />
        </div>
        {raise.is_placeholder && <p className="mt-4 text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Figures are placeholders</p>}
      </div>
    </Panel>
  );
}

function Breakdown({ label, value, dot }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="inline-flex items-center gap-2"><SectionLabel>{label}</SectionLabel>{dot && <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />}</span>
      <span className="font-mono tabular-nums text-sm text-foreground">{value}</span>
    </div>
  );
}

// ---- Runway / cash -----------------------------------------------------------
function CashEditDialog({ raise, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cash, setCash] = useState(raise.cash_on_hand);
  const [burn, setBurn] = useState(raise.monthly_burn);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/capital/raise", {
        cash_on_hand: cash === "" ? 0 : parseFloat(cash),
        monthly_burn: burn === "" ? 0 : parseFloat(burn),
      });
      onSaved(res.data);
      toast.success("Cash position updated");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update cash position");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="cash-edit-button" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
          <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} /> Edit
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-display text-lg">Cash and burn</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Cash on hand</Label>
            <Input type="number" value={cash ?? 0} onChange={(e) => setCash(e.target.value)} className="font-mono" /></div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Monthly burn</Label>
            <Input type="number" value={burn ?? 0} onChange={(e) => setBurn(e.target.value)} className="font-mono" /></div>
        </div>
        <DialogFooter>
          <Button data-testid="cash-save" onClick={save} disabled={saving} className="bg-primary hover:bg-[#2746c0]">{saving ? "Saving" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RunwayCash({ raise, onSaved }) {
  const cur = raise.currency || "SGD";
  return (
    <Panel title="Runway and cash" testId="runway-cash" action={raise.can_edit ? <CashEditDialog raise={raise} onSaved={onSaved} /> : null}>
      <div className="p-4 space-y-4">
        <div>
          <SectionLabel>Blended runway</SectionLabel>
          <p data-testid="runway-value" className="mt-1 font-mono tabular-nums text-3xl text-foreground leading-none">
            {raise.runway_months != null ? `${raise.runway_months}` : EMPTY_MARK}<span className="text-base text-muted-foreground"> months</span>
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4 border-t border-border pt-4">
          <div><SectionLabel>Cash on hand</SectionLabel><p className="mt-1 font-mono tabular-nums text-sm text-foreground">{moneyFull(raise.cash_on_hand, cur)}</p></div>
          <div><SectionLabel>Monthly burn</SectionLabel><p className="mt-1 font-mono tabular-nums text-sm text-foreground">{moneyFull(raise.monthly_burn, cur)}</p></div>
        </div>
        <p className="text-[11px] text-muted-foreground">Runway equals cash on hand divided by monthly burn. This figure feeds the cockpit.</p>
      </div>
    </Panel>
  );
}

// ---- Investor pipeline -------------------------------------------------------
function InvestorDialog({ investor, people, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const editing = !!investor;
  const [f, setF] = useState(
    investor || { name: "", stage: "Sourced", ticket_size: 0, instrument: "SAFE", owner_person_id: "", next_action: "", notes: "" }
  );
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...f, ticket_size: parseFloat(f.ticket_size) || 0, owner_person_id: f.owner_person_id || null };
      if (editing) await api.patch(`/capital/investors/${investor.id}`, payload);
      else await api.post("/capital/investors", payload);
      toast.success(editing ? "Investor updated" : "Investor added");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save investor");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-lg">{editing ? "Edit investor" : "Add investor"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Name</Label>
            <Input data-testid="investor-name-input" value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Stage</Label>
              <Select value={f.stage} onValueChange={(v) => set("stage", v)}>
                <SelectTrigger data-testid="investor-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Instrument</Label>
              <Select value={f.instrument} onValueChange={(v) => set("instrument", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{INSTRUMENTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Ticket size</Label>
              <Input type="number" value={f.ticket_size ?? 0} onChange={(e) => set("ticket_size", e.target.value)} className="font-mono" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Owner</Label>
              <Select value={f.owner_person_id || ""} onValueChange={(v) => set("owner_person_id", v)}>
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Next action</Label>
            <Input value={f.next_action} onChange={(e) => set("next_action", e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Notes</Label>
            <Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button data-testid="investor-save" onClick={save} disabled={saving} className="bg-primary hover:bg-[#2746c0]">{saving ? "Saving" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LogContactDialog({ investor, onSaved }) {
  const [open, setOpen] = useState(false);
  const [next, setNext] = useState(investor.next_action || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/capital/investors/${investor.id}/log-contact`, { next_action: next });
      toast.success("Contact logged");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error("Could not log contact"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`investor-logcontact-${investor.id}`} title="Log contact" className="text-muted-foreground hover:text-foreground"><PhoneCall className="h-4 w-4" strokeWidth={1.75} /></button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-display text-lg">Log contact, {investor.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">This sets last contact to today.</p>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Next action</Label>
            <Input value={next} onChange={(e) => setNext(e.target.value)} /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} className="bg-primary hover:bg-[#2746c0]">{saving ? "Saving" : "Log"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvestorPipeline({ data, people, currency, onChanged }) {
  const canEdit = data.can_edit;
  return (
    <Panel
      title="Investor pipeline"
      testId="investor-pipeline"
      action={canEdit ? (
        <InvestorDialog people={people} onSaved={onChanged}
          trigger={<button data-testid="add-investor-button" className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-[#5b78e8]"><Plus className="h-3.5 w-3.5" strokeWidth={2} /> Add investor</button>} />
      ) : <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Read only</span>}
    >
      {data.investors.length === 0 ? (
        <EmptyState message="No investors in the pipeline yet." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.1em] text-muted-foreground border-b border-border">
                <th className="px-4 py-2.5 font-medium">Investor</th>
                <th className="px-4 py-2.5 font-medium">Stage</th>
                <th className="px-4 py-2.5 font-medium text-right">Ticket</th>
                <th className="px-4 py-2.5 font-medium">Instrument</th>
                <th className="px-4 py-2.5 font-medium">Owner</th>
                <th className="px-4 py-2.5 font-medium">Last contact</th>
                <th className="px-4 py-2.5 font-medium">Next action</th>
                {canEdit && <th className="px-4 py-2.5 font-medium text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.investors.map((inv) => (
                <tr key={inv.id} data-testid="investor-row" className="hover:bg-white/[0.03]">
                  <td className="px-4 py-3 text-foreground">{inv.name}</td>
                  <td className="px-4 py-3"><StatusPill dot={false} tone={stageTone(inv.stage)} label={inv.stage} /></td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-foreground">{moneyFull(inv.ticket_size, currency)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{inv.instrument}</td>
                  <td className="px-4 py-3 text-muted-foreground">{inv.owner_name || EMPTY_MARK}</td>
                  <td className="px-4 py-3 font-mono text-muted-foreground">{inv.last_contact ? shortDay(inv.last_contact) : EMPTY_MARK}</td>
                  <td className="px-4 py-3 text-muted-foreground max-w-[220px] truncate">{inv.next_action || EMPTY_MARK}</td>
                  {canEdit && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-3">
                        <LogContactDialog investor={inv} onSaved={onChanged} />
                        <InvestorDialog investor={inv} people={people} onSaved={onChanged}
                          trigger={<button data-testid={`investor-edit-${inv.id}`} title="Edit" className="text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" strokeWidth={1.75} /></button>} />
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button data-testid={`investor-delete-${inv.id}`} title="Remove" className="text-muted-foreground hover:text-[#EF4444]"><Trash2 className="h-4 w-4" strokeWidth={1.75} /></button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove {inv.name}?</AlertDialogTitle>
                              <AlertDialogDescription>This removes the investor from the pipeline. This cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-[#b91c1c] hover:bg-[#a01818]"
                                onClick={async () => {
                                  try { await api.delete(`/capital/investors/${inv.id}`); toast.success("Investor removed"); onChanged(); }
                                  catch (e) { toast.error("Could not remove investor"); }
                                }}>Remove</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

// ---- Raise documents ---------------------------------------------------------
function NewVersionDialog({ doc, onSaved }) {
  const [open, setOpen] = useState(false);
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/documents/${doc.id}/versions`, { file_reference: ref, note });
      toast.success("New version recorded");
      setOpen(false); setRef(""); setNote("");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add version"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`doc-version-${doc.id}`} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"><History className="h-3.5 w-3.5" strokeWidth={1.75} /> New version</button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-display text-lg">New version, {doc.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">File reference</Label>
            <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder={`v${(doc.version || 0) + 1}`} className="font-mono" /></div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} className="bg-primary hover:bg-[#2746c0]">{saving ? "Saving" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewDocDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", doc_type: "Deck", file_reference: "", summary: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      await api.post("/documents", { ...f, arm: "Investments and Capital", access_level: "studio" });
      toast.success("Document added");
      setOpen(false); setF({ title: "", doc_type: "Deck", file_reference: "", summary: "" });
      onSaved();
    } catch (e) { toast.error("Could not add document"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-raise-doc-button" className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-[#5b78e8]"><FilePlus2 className="h-3.5 w-3.5" strokeWidth={1.75} /> Add document</button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-lg">Add raise document</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Title</Label>
            <Input value={f.title} onChange={(e) => set("title", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Type</Label>
              <Select value={f.doc_type} onValueChange={(v) => set("doc_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Deck", "One-pager", "Memo", "Template", "Brief"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">File reference</Label>
              <Input value={f.file_reference} onChange={(e) => set("file_reference", e.target.value)} className="font-mono" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Summary</Label>
            <Textarea value={f.summary} onChange={(e) => set("summary", e.target.value)} rows={2} /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} className="bg-primary hover:bg-[#2746c0]">{saving ? "Saving" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RaiseDocuments({ docs, canCreate, onChanged }) {
  return (
    <Panel title="Raise documents" testId="raise-documents" action={canCreate ? <NewDocDialog onSaved={onChanged} /> : null}>
      {docs.length === 0 ? (
        <EmptyState message="No raise materials yet." />
      ) : (
        <ul className="divide-y divide-border">
          {docs.map((d) => (
            <li key={d.id} data-testid="raise-doc-row" className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm text-foreground truncate">{d.title} <span className="font-mono text-xs text-muted-foreground">v{d.version}</span></p>
                <p className="text-xs text-muted-foreground truncate">{d.doc_type} <span className="text-muted-foreground/50">/</span> {d.file_reference}</p>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <StatusPill dot={false} tone={d.access_level === "confidential" ? "blocked" : "neutral"} label={d.access_level === "confidential" ? "Confidential" : "Studio"} />
                {d.can_edit && <NewVersionDialog doc={d} onSaved={onChanged} />}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ---- Page --------------------------------------------------------------------
export default function Capital() {
  const [raise, setRaise] = useState(null);
  const [investors, setInvestors] = useState(null);
  const [people, setPeople] = useState([]);
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restricted, setRestricted] = useState(false);

  const loadRaise = useCallback(async () => {
    const res = await api.get("/capital/raise"); setRaise(res.data);
  }, []);
  const loadInvestors = useCallback(async () => {
    const res = await api.get("/capital/investors"); setInvestors(res.data);
  }, []);
  const loadDocs = useCallback(async () => {
    const res = await api.get("/documents", { params: { arm: "Investments and Capital" } });
    setDocs(res.data);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, i, d, p] = await Promise.all([
        api.get("/capital/raise"),
        api.get("/capital/investors"),
        api.get("/documents", { params: { arm: "Investments and Capital" } }),
        api.get("/people").catch(() => ({ data: { people: [] } })),
      ]);
      setRaise(r.data); setInvestors(i.data); setDocs(d.data); setPeople(p.data.people || []);
    } catch (e) {
      if (e?.response?.status === 403) setRestricted(true);
      else toast.error("Could not load Capital.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (loading) return <Loading label="Loading capital" />;
  if (restricted) {
    return (
      <div className="rounded-md border border-border bg-card px-6 py-16 text-center">
        <Lock className="mx-auto h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
        <h2 className="mt-4 font-display text-xl text-foreground">Capital is restricted</h2>
        <p className="mt-2 text-sm text-muted-foreground">This area is available to the studio team.</p>
      </div>
    );
  }
  if (!raise) return <EmptyState message="No capital data." />;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-8"><RaiseTracker raise={raise} onSaved={(d) => setRaise(d)} /></div>
        <div className="col-span-12 lg:col-span-4"><RunwayCash raise={raise} onSaved={(d) => setRaise(d)} /></div>
      </div>
      {investors && (
        <InvestorPipeline data={investors} people={people} currency={raise.currency} onChanged={() => { loadInvestors(); loadRaise(); }} />
      )}
      {docs && <RaiseDocuments docs={docs.documents} canCreate={docs.can_create} onChanged={loadDocs} />}
    </div>
  );
}
